import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  registerServiceWorker,
  isPWAMode,
  getAppVersion,
} from '../../services/pwa';

interface PWAContextType {
  isPWAMode: boolean;
  isOnline: boolean;
  appVersion: string | null;
  isLoading: boolean;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

interface PWAProviderProps {
  children: ReactNode;
}

export const PWAProvider: React.FC<PWAProviderProps> = ({ children }) => {
  const [pwaMode, setPWAMode] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializePWA = async () => {
      try {
        // Register service worker
        const swRegistered = await registerServiceWorker();
        console.log('[PWA] Service worker registration result:', swRegistered);

        // Check if app is in PWA mode
        const pwaModeResult = isPWAMode();
        setPWAMode(pwaModeResult);

        // Get app version with timeout to prevent hanging when no service worker
        let version: string | null = null;
        try {
          const versionPromise = getAppVersion();
          const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000));
          version = await Promise.race([versionPromise, timeoutPromise]);
        } catch {
          version = null;
        }
        setAppVersion(version);

        console.log('[PWA] Initialized:', {
          swRegistered,
          pwaMode: pwaModeResult,
          version,
          isOnline: navigator.onLine,
          protocol: window.location.protocol,
          hostname: window.location.hostname,
        });
      } catch (error) {
        console.error('[PWA] Initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializePWA();
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('[PWA] App went online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[PWA] App went offline');
    };

    const handleDisplayModeChange = () => {
      const pwaModeResult = isPWAMode();
      setPWAMode(pwaModeResult);
      console.log('[PWA] Display mode changed:', pwaModeResult);
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const value: PWAContextType = {
    isPWAMode: pwaMode,
    isOnline,
    appVersion,
    isLoading,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
};

export const usePWA = (): PWAContextType => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export default PWAProvider;
